/**
 * Workflow form page (create/edit) — CoordioCore form-page-pattern.
 *
 * Layout: PageFormLayout (ShellHeader + SectionSideNav grid + panel sections)
 * Delegates field rendering to WorkflowDesigner, which returns section content
 * and form state for the layout to consume.
 */

// React / Router Imports
import { useParams, useNavigate } from 'react-router-dom';
import { FileText, Code2, Settings } from 'lucide-react';

// Layout Import
import { PageFormLayout } from '../components/layouts/PageFormLayout';

// Component Imports
import { WorkflowDesigner } from '../components/workflows/WorkflowDesigner';

/**
 * Workflow form page (create/edit).
 */
export const WorkflowFormPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;

  const designer = WorkflowDesigner({
    workflowId: id,
    onSave: () => navigate('/workflows'),
    onCancel: () => navigate('/workflows'),
  });

  return (
    <PageFormLayout
      title={isEdit ? 'Edit Workflow' : 'Create Workflow'}
      subtitle="Define the workflow steps, triggers, and configuration."
      data-testid="workflow-form-form-page"
      loading={designer.loading}
      error={designer.error ?? undefined}
      onErrorDismiss={() => designer.setError(null)}
      backTo={{ label: 'Back to List', href: '/workflows' }}
      actions={{
        onSave: designer.handleSave,
        onCancel: () => navigate('/workflows'),
        saveLabel: isEdit ? 'Save changes' : 'Create',
        saving: designer.saving,
      }}
      navGroups={[
        {
          title: 'Profile',
          items: [
            { id: 'details', label: 'Details', icon: <FileText size={14} /> },
          ],
        },
        {
          title: 'Configuration',
          items: [
            { id: 'definition', label: 'Definition', icon: <Code2 size={14} /> },
            { id: 'capabilities', label: 'Capabilities', icon: <Settings size={14} /> },
          ],
        },
      ]}
      sections={[
        {
          id: 'details',
          title: 'Details',
          description: 'Workflow name, description, type, and pattern configuration.',
          icon: <FileText size={16} />,
          content: designer.detailsContent,
        },
        {
          id: 'definition',
          title: 'Definition',
          description: 'JSON-based workflow definition with nodes and edges.',
          icon: <Code2 size={16} />,
          content: designer.definitionContent,
        },
        {
          id: 'capabilities',
          title: 'Capabilities',
          description: 'Runtime feature toggles for checkpointing and streaming.',
          icon: <Settings size={16} />,
          content: designer.capabilitiesContent,
        },
      ]}
    />
  );
};
